import { useState, type ReactNode } from 'react';
import { useLocation, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router';
import { useSite } from '@/app/site-context';
import { usePublicPage } from '@/lib/api/queries';
import { useDocumentHead } from '@/lib/seo/useDocumentHead';
import {
  ButtonLink,
  Card,
  Chip,
  Container,
  IconBubble,
  Section,
  SectionHeading,
  Skeleton,
} from '@/components/ui';
import { RichTextBody } from '@/components/content/RichTextBody';
import {
  AboutSection,
  CapabilityGrid,
  CtaBand,
  FocusPillars,
  MissionVision,
  OurStory,
  RecruitmentProcess,
  SERVICES,
  ServiceCards,
  ServiceDetailBody,
  StrategicFocus,
  TeamSection,
} from '@/components/marketing/sections';
import { TeamVoices, type TeamVoiceFocus } from '@/components/marketing/TeamVoices';
import { NotFoundPage } from './NotFoundPage';
import { PageHero } from '@/components/marketing/PageHero';

// -----------------------------------------------------------------------------
// Services index
// -----------------------------------------------------------------------------

export function ServicesPage(): ReactNode {
  const { settings } = useSite();

  useDocumentHead({
    title: `Services | ${settings.brand.name}`,
    description:
      'Executive search, permanent hiring, recruitment process outsourcing and on-demand talent sourcing.',
    canonicalPath: '/services',
  });

  return (
    <>
      <PageHero
        eyebrow="Services"
        title="Four ways we help you hire"
        highlight="help you hire"
        description="Every engagement starts the same way: a written brief we both agree describes the same role. What differs is how much of the hiring you want us to run."
        actions={
          <>
            <ButtonLink to="/contact" size="lg">
              Talk to us about a role
            </ButtonLink>
            <ButtonLink to="/about" variant="secondary" size="lg">
              How we work
            </ButtonLink>
          </>
        }
      />

      <ServiceCards heading={false} className="pt-10 md:pt-14" />
      {/*
        The four cards say *what* you can buy; this says what buying any of them looks
        like. It sits directly under them because "which service is mine?" and "what
        happens then?" are the two questions a visitor arrives with, in that order.
      */}
      <RecruitmentProcess />
      <CapabilityGrid />
      <StrategicFocus />
      <CtaBand />
    </>
  );
}

// -----------------------------------------------------------------------------
// Service detail
// -----------------------------------------------------------------------------

/**
 * A single service page.
 *
 * All four service URLs are STATIC page rows pointing at this one component, so the
 * service is identified from the URL rather than from a separate route per service. That
 * keeps the route manifest data-driven — an admin can rename or hide any of the four
 * without a code change — while the content stays in code where it belongs.
 */
export function ServiceDetailPage(): ReactNode {
  const location = useLocation();
  const { settings } = useSite();

  const slug = location.pathname.split('/').filter(Boolean).pop() ?? '';
  const service = SERVICES.find((entry) => entry.slug === slug);

  useDocumentHead({
    title: service ? `${service.title} | ${settings.brand.name}` : 'Service',
    description: service?.summary,
    canonicalPath: location.pathname,
  });

  // A page row pointing at a service slug the code does not know about. Renders the
  // real 404 rather than a blank page, which is the honest answer.
  if (!service) return <NotFoundPage />;

  return (
    <>
      <PageHero
        // The service's own name becomes the eyebrow, so the h1 can be a sentence about
        // the work rather than a label the visitor already read on the card they clicked.
        eyebrow={service.title}
        title={service.headline}
        highlight={service.headlineHighlight}
        description={service.summary}
        note={service.idealFor}
        above={
          <div className="flex flex-col items-center gap-5">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-sm font-semibold text-primary-strong hover:underline"
            >
              <ArrowLeft className="size-4" />
              All services
            </Link>
            {/* Same icon the homepage card uses — `SERVICES` is the one source. */}
            <IconBubble className="size-14">
              <service.icon className="size-6" />
            </IconBubble>
            {/*
              The client's own name for the service, where it differs from ours. Shown as
              a chip rather than folded into the eyebrow: the eyebrow is the page's label
              and has to match the navigation item that brought the visitor here, and two
              names separated by a middot stop reading as a label at all.
            */}
            {service.alias === null ? null : (
              <Chip tone="neutral">Also known as {service.alias}</Chip>
            )}
          </div>
        }
        actions={
          <>
            <ButtonLink to="/contact" size="lg">
              Talk to us about a role
            </ButtonLink>
            <ButtonLink to="/services" variant="secondary" size="lg">
              View all services
            </ButtonLink>
          </>
        }
      />

      <ServiceDetailBody service={service} />

      <ServiceCards heading />
      <CtaBand />
    </>
  );
}

// -----------------------------------------------------------------------------
// About
// -----------------------------------------------------------------------------

export function AboutPage(): ReactNode {
  const { settings } = useSite();

  useDocumentHead({
    title: `About | ${settings.brand.name}`,
    description:
      'A recruitment boutique based in Dubai with an entity in India, hiring across the UAE, India, Europe, Africa and the United States.',
    canonicalPath: '/about',
  });

  return (
    <>
      <PageHero
        eyebrow="About us"
        title="A boutique, deliberately"
        highlight="deliberately"
        description="Small enough that you deal with the people doing the work, and experienced enough to tell you when a brief needs rethinking rather than more CVs."
        actions={
          <>
            <ButtonLink to="/contact" size="lg">
              Talk to us about a role
            </ButtonLink>
            <ButtonLink to="/services" variant="secondary" size="lg">
              What we do
            </ButtonLink>
          </>
        }
      />

      <AboutSection className="pt-10 md:pt-14" />
      {/*
        What the firm intends, then the three things it holds to, then how it got here.

        No team preview after the story any more. Team is its own menu entry and page, so
        three faces here were a second, worse door to it — and a screen of scrolling on a
        page that is about the firm rather than the people.
      */}
      <MissionVision />
      <FocusPillars />
      <OurStory />
      <CtaBand />
    </>
  );
}

// -----------------------------------------------------------------------------
// The full team
// -----------------------------------------------------------------------------

/**
 * Everybody, on one page.
 *
 * The only place the team appears. The homepage and About used to carry three-person
 * previews; with Team in the menu they were second doors to this page, so they went — one
 * canonical address for "who works here", and one place to keep it right.
 */
export function TeamPage(): ReactNode {
  const { settings } = useSite();
  const team = settings.team;
  const [focus, setFocus] = useState<TeamVoiceFocus | null>(null);

  useDocumentHead({
    title: `${team.heading} | ${settings.brand.name}`,
    description:
      'The people you work with: what they specialise in, where they have hired, and how to reach them.',
    canonicalPath: '/team',
  });

  return (
    <>
      <PageHero
        eyebrow="Who you work with"
        title={team.heading}
        description="A boutique means the people you meet are the people running your search. No account manager between you and the person doing the work."
        actions={
          <>
            <ButtonLink to="/contact" size="lg">
              Talk to us about a role
            </ButtonLink>
            <ButtonLink to="/about" variant="secondary" size="lg">
              About the firm
            </ButtonLink>
          </>
        }
      />

      {/*
        The grid hands a name up; the band below takes it. Keeping the selection here
        rather than inside either component is what lets a card scroll to a section it
        does not contain and knows nothing about.
      */}
      <TeamSection
        onSelectMember={(name) =>
          // The counter, not the name, is what makes a repeat click register — see
          // `TeamVoiceFocus`.
          setFocus((current) => ({ name, nonce: (current?.nonce ?? 0) + 1 }))
        }
      />
      {/*
        After the grid rather than before it: a quote from somebody you have not met yet is
        an anecdote, and the same quote from a face you just scrolled past is a reference.
      */}
      <TeamVoices focus={focus} />
      {/*
        The "Join us" invitation closes the page beside the client call to action, rather
        than as a card in the grid of people — see `CtaBand`.
      */}
      <CtaBand joinUs />
    </>
  );
}

// -----------------------------------------------------------------------------
// Careers (Phase 2)
// -----------------------------------------------------------------------------

/**
 * The careers page.
 *
 * Built, but its page row ships hidden — the client deferred careers and job posting to
 * Phase 2. It exists so that phase begins with a toggle rather than a build, and so the
 * route, navigation entry and SEO plumbing are already proven.
 *
 * While the page is hidden, the route is not in the manifest, so this component is
 * unreachable. Revealing it in the admin makes it live immediately.
 */
export function CareersPage(): ReactNode {
  const { settings } = useSite();

  useDocumentHead({
    title: `Careers | ${settings.brand.name}`,
    description: 'Open roles at The BlackBox Talent.',
    canonicalPath: '/careers',
  });

  return (
    <>
      <PageHero
        eyebrow="Careers"
        title="Work with us"
        highlight="with us"
        description="We hire talent acquisition specialists who would rather tell a client the truth than fill a role badly."
      />

      <Section tone="surface" className="pt-10 md:pt-14">
        <Container className="max-w-3xl">
          <Card className="flex flex-col gap-4">
            <h2 className="text-h3 font-semibold text-heading">No open roles listed yet</h2>
            <p className="text-body">
              Job posting is planned for the next phase of this website. Until then, send us a note
              and we will keep it on file.
            </p>
            <ButtonLink to="/contact" className="self-start">
              Get in touch
            </ButtonLink>
          </Card>
        </Container>
      </Section>
    </>
  );
}

// -----------------------------------------------------------------------------
// CMS-authored page
// -----------------------------------------------------------------------------

/**
 * Renders a CUSTOM page — one the team created in the admin with a rich-text body.
 *
 * This is what makes "add a new page" possible without a developer: one template, any
 * number of pages, each resolved by path at runtime.
 */
export function CustomPage(): ReactNode {
  const location = useLocation();
  const path = location.pathname.replace(/^\/+|\/+$/g, '');

  const { data: page, isLoading, isError } = usePublicPage(path);

  useDocumentHead({
    title: page?.seo.title ?? 'Loading…',
    description: page?.seo.description,
    canonicalPath: location.pathname,
    noIndex: page?.seo.noIndex,
  });

  if (isLoading) {
    return (
      <Section size="hero">
        <Container className="flex max-w-3xl flex-col gap-6">
          <Skeleton className="h-12 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-4/6" />
        </Container>
      </Section>
    );
  }

  if (isError || !page) return <NotFoundPage />;

  return (
    <>
      <PageHero title={page.title} />

      <Section tone="surface" size="compact" className="pt-10 md:pt-14">
        <Container className="max-w-3xl">
          <RichTextBody html={page.bodyHtml} />
        </Container>
      </Section>
    </>
  );
}

/** Wrapper so the router can pass a title for the legal placeholder pages. */
export function LegalPage(): ReactNode {
  const { pathname } = useLocation();
  const { routes } = useSite();
  const params = useParams();

  const path = pathname.replace(/^\/+|\/+$/g, '');
  const route = routes.find((entry) => entry.path === path);

  void params;

  return <LegalContent title={route?.title ?? 'Legal'} path={path} />;
}

function LegalContent({ title, path }: { title: string; path: string }): ReactNode {
  useDocumentHead({ title, canonicalPath: `/${path}`, noIndex: true });

  return (
    <Section size="hero">
      <Container className="max-w-3xl">
        <SectionHeading as="h1" align="left" title={title} />
        <Card className="mt-10 flex flex-col gap-4">
          <p className="text-body">
            This page is awaiting its content. It is intentionally empty rather than filled with
            template wording: a legal notice has to describe what this business actually does, and
            boilerplate that looks finished is worse than a visible gap.
          </p>
          <p className="text-sm text-muted">
            An administrator can publish real content for this page from the admin console. Until
            then it is marked <code>noindex</code>.
          </p>
        </Card>
      </Container>
    </Section>
  );
}
